import { getHost } from "./getHost";

// Look up a single Unleash flag from the host-supplied `featureFlags`
// dict. Mirrors the presenter's `featureToggles/isFeatureEnabled` getter
// (vuex/modules/featureToggles.module.js:401-403) — the same name lookup
// + `.enabled` check.
//
// Why a plain function rather than a React hook: zoid xprops are
// hydrated before any React render and don't change for the lifetime of
// the embed session (the host passes a static snapshot). A hook would
// imply reactivity we don't have. Wrap in `useMemo` at the callsite if
// you want a stable reference.
//
// Standalone dev (no host) returns false for every flag — features that
// must remain visible should not be flagged, or the dev should pass a
// mock via `xprops` overrides.
export function isHostFeatureEnabled(featureName: string): boolean {
  const flag = getHost()?.featureFlags?.[featureName];
  return Boolean(flag?.enabled);
}

// AHA-42627: lets specific SCIM-provisioned teams (ATB and a few others)
// still see manual-management controls — invite, role change, license
// change, remove. Stays in lockstep with the presenter Vuex getter
// `featureToggles/canUseScimManualManagement`.
export const SCIM_MANUAL_MANAGEMENT_FLAG = "AHA-42627-scim-manual-management";

// AHA-39565: gates the unverified-email badge on member rows. Same flag the
// presenter uses (featureToggles getter `canUseEmailVerificationComponent`),
// so the host's `featureFlags` snapshot already carries it — the badge lights
// up in lockstep with the presenter's rollout.
export const EMAIL_VERIFICATION_FLAG = "AHA-39565-release-email-verification-component";

// The flags the app currently consumes, with human-readable labels. The
// DevHostPanel renders a toggle per entry so a tester can flip the known
// gated UI on standalone/in the sandbox. Add new app-consumed flags here so
// the dev overlay stays in lockstep with what the code actually reads — this
// is the single list of "flags worth surfacing by default".
export interface KnownFeatureFlag {
  name: string;
  label: string;
}

export const KNOWN_FEATURE_FLAGS: readonly KnownFeatureFlag[] = [
  { name: SCIM_MANUAL_MANAGEMENT_FLAG, label: "SCIM manual management" },
  { name: EMAIL_VERIFICATION_FLAG, label: "Email verification component" },
];
